// Adjectives — agreement, position, comparison, possessives, plus descriptive
// and color vocabulary tables. Ported from the legacy views.adjectives;
// VOCABULARY is imported directly (old shape: { pt, en, cat }).
import { VOCABULARY } from "../data/index.js";
import { renderGuideSections } from "../ui/guideSections.js";

export function adjectivesView(app) {
  const adjs = VOCABULARY.filter((v) => v.cat === "adj");
  const colors = VOCABULARY.filter((v) => v.cat === "color");

  const tables = `
    <div class="grid sm:grid-cols-2 gap-4 mt-6">
      <div class="bg-white rounded-2xl border border-sage-100 p-5">
        <h3 class="text-lg font-bold text-sage-800 mb-3">Descriptive Adjectives</h3>
        <div class="overflow-x-auto"><table class="w-full text-sm">
          <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
            <th class="py-2 pr-3">Portuguese</th><th class="py-2 px-3">English</th>
          </tr></thead>
          <tbody>
            ${adjs
              .map(
                (a) => `<tr class="border-b border-sage-50 last:border-0">
              <td class="py-2 pr-3 font-medium text-sage-800">${a.pt}</td>
              <td class="py-2 px-3 text-sage-700">${a.en}</td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table></div>
      </div>
      <div class="bg-white rounded-2xl border border-sage-100 p-5">
        <h3 class="text-lg font-bold text-sage-800 mb-3">Colors</h3>
        <div class="overflow-x-auto"><table class="w-full text-sm">
          <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
            <th class="py-2 pr-3">Portuguese</th><th class="py-2 px-3">English</th>
          </tr></thead>
          <tbody>
            ${colors
              .map(
                (c) => `<tr class="border-b border-sage-50 last:border-0">
              <td class="py-2 pr-3 font-medium text-sage-800">${c.pt}</td>
              <td class="py-2 px-3 text-sage-700">${c.en}</td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table></div>
      </div>
    </div>
  `;

  renderGuideSections(
    app,
    "Adjectives",
    "Adjectives describe nouns — and in Portuguese, they have to match the noun in gender and number. They also usually come after the noun, not before.",
    [
      {
        title: "1. Agreement — gender & number",
        body: `
          <p class="text-sage-700 mb-3">Adjectives change their ending to match the noun they describe.</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Type</th><th class="py-2 px-3">M. sg</th><th class="py-2 px-3">F. sg</th><th class="py-2 px-3">M. pl</th><th class="py-2 px-3">F. pl</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">-o / -a</td><td class="py-2 px-3">alto</td><td class="py-2 px-3">alta</td><td class="py-2 px-3">altos</td><td class="py-2 px-3">altas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">-e (one form)</td><td class="py-2 px-3">grande</td><td class="py-2 px-3">grande</td><td class="py-2 px-3">grandes</td><td class="py-2 px-3">grandes</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Consonant</td><td class="py-2 px-3">feliz</td><td class="py-2 px-3">feliz</td><td class="py-2 px-3">felizes</td><td class="py-2 px-3">felizes</td></tr>
              <tr><td class="py-2 pr-3 font-medium">Nationalities</td><td class="py-2 px-3">português</td><td class="py-2 px-3">portuguesa</td><td class="py-2 px-3">portugueses</td><td class="py-2 px-3">portuguesas</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm">Examples: <i>o carro vermelho, a casa vermelha, os carros vermelhos, as casas vermelhas</i>.</p>
        `,
      },
      {
        title: "2. Position — after the noun (usually)",
        body: `
          <p class="text-sage-700 mb-2">Default position is <b>after</b> the noun: <i>uma casa grande</i>, <i>um livro interessante</i>.</p>
          <p class="text-sage-700 mb-2">Some adjectives change meaning depending on position:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>um <b>grande</b> homem</i> = a great man · <i>um homem <b>grande</b></i> = a big man</li>
            <li><i>um <b>velho</b> amigo</i> = an old (long-time) friend · <i>um amigo <b>velho</b></i> = an elderly friend</li>
            <li><i>o <b>pobre</b> homem</i> = the poor (pitied) man · <i>o homem <b>pobre</b></i> = the poor (broke) man</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>A few short forms before the noun:</b> <i>bom/boa</i> usually precedes (<i>um bom livro</i>); <i>grande</i> before a noun means "great" (<i>um grande artista</i>); <i>santo</i> shortens to <i>são</i> before a male saint's name (<i>São Pedro</i>).</p>
        `,
      },
      {
        title: "3. Comparison",
        body: `
          <ul class="text-sage-700 text-sm space-y-2 list-disc pl-5">
            <li><b>More than:</b> <i>mais + adj + (do) que</i> → <i>A Maria é <b>mais alta do que</b> o João.</i></li>
            <li><b>Less than:</b> <i>menos + adj + (do) que</i> → <i>O livro é <b>menos caro do que</b> o outro.</i></li>
            <li><b>As ... as:</b> <i>tão + adj + como</i> → <i>É <b>tão rápido como</b> tu.</i></li>
            <li><b>Superlative:</b> <i>o/a/os/as mais + adj</i> → <i>a mais bonita</i>.</li>
            <li><b>Irregulars:</b> bom → <b>melhor</b>, mau → <b>pior</b>, grande → <b>maior</b>, pequeno → <b>menor</b>.</li>
            <li><b>Absolute superlative (-íssimo):</b> <i>grande → grandíssimo, fácil → facílimo</i>.</li>
          </ul>
        `,
      },
      {
        title: "4. Possessive adjectives",
        body: `
          <div class="overflow-x-auto"><table class="w-full text-sm">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Owner</th><th class="py-2 px-3">Singular</th><th class="py-2 px-3">Plural</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">my</td><td class="py-2 px-3">meu / minha</td><td class="py-2 px-3">meus / minhas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">your (tu)</td><td class="py-2 px-3">teu / tua</td><td class="py-2 px-3">teus / tuas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">his/her/your (você)</td><td class="py-2 px-3">seu / sua</td><td class="py-2 px-3">seus / suas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">our</td><td class="py-2 px-3">nosso / nossa</td><td class="py-2 px-3">nossos / nossas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">your (vós)</td><td class="py-2 px-3">vosso / vossa</td><td class="py-2 px-3">vossos / vossas</td></tr>
              <tr><td class="py-2 pr-3 font-medium">their/your (vocês)</td><td class="py-2 px-3">seu / sua</td><td class="py-2 px-3">seus / suas</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm mt-3">Agreement is with the <b>thing owned</b>, not the owner: <i>a nossa casa</i> (our house — feminine), <i>os nossos livros</i> (our books — masculine plural). European Portuguese normally keeps the definite article before the possessive: <i>o meu carro</i>, not just <i>meu carro</i>.</p>
        `,
      },
    ],
    "",
    tables
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
